<template>
  <v-dialog v-model="value" max-width="800px" persistent>
    <v-card :loading="loading" :disabled="loading">
      <v-card-title class="justify-center">
        <span class="text-h5 sp">{{ $t('teachers.create') }}</span>
      </v-card-title>

      <v-card-text>
        <v-container v-if="!created">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.baseCountryId"
                  :items="countries"
                  item-text="name"
                  item-value="id"
                  :label="$t('teachers.country')"
                  :rules="[rules.required]"
                  :disabled="lockContext"
                  @change="onCountryChange"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.schoolId"
                  :items="schools"
                  item-text="text"
                  item-value="value"
                  :label="$t('teachers.school')"
                  :rules="[rules.required]"
                  :disabled="lockContext || !form.baseCountryId"
                  @change="onSchoolChange"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="courseSearch"
                  :items="availableCourses"
                  item-text="displayName"
                  item-value="id"
                  :label="$t('teachers.courses')"
                  :disabled="!form.schoolId"
                  prepend-inner-icon="mdi-magnify"
                  :no-data-text="$t('teachers.no_courses_available')"
                  clearable
                  @change="addCourse"
                />
              </v-col>
            </v-row>

            <v-row v-if="form.courseIds.length" class="mt-0">
              <v-col cols="12" class="pt-0">
                <div class="d-flex flex-wrap" style="gap: 8px;">
                  <v-chip
                    v-for="course in selectedCourses"
                    :key="course.id"
                    label
                    close
                    close-icon="mdi-close"
                    color="deep-purple lighten-5"
                    @click:close="removeCourse(course.id)"
                  >
                    {{ course.displayName }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.firstname"
                  :label="$t('teachers.firstname')"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.lastname"
                  :label="$t('teachers.lastname')"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.lastname2nd"
                  :label="$t('teachers.lastname2nd')"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.email"
                  :label="$t('teachers.email')"
                  :rules="[rules.email]"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.dni"
                  :label="$t('teachers.dni')"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.birthDate"
                      :label="$t('teachers.birth_date')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      clearable
                    />
                  </template>
                  <v-date-picker v-model="form.birthDate" @input="dateMenu = false" />
                </v-menu>
              </v-col>
            </v-row>
          </v-form>
        </v-container>

        <v-container v-else class="text-center">
          <v-icon size="48" color="green">mdi-check-circle</v-icon>
          <h3 class="mt-2">{{ $t('teachers.created_title') }}</h3>
          <p class="mt-2">{{ createdCredentials }}</p>
          <p class="grey--text">{{ $t('teachers.created_must_change') }}</p>
          <p v-if="!createdInfo.hasEmail" class="orange--text text--darken-2">
            {{ $t('teachers.created_no_email_notice') }}
          </p>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <template v-if="!created">
          <v-btn color="grey" text @click="close">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="deep-purple" text :disabled="!valid" @click="save">
            {{ $t('common.save') }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn color="deep-purple" text @click="resetForAnother">
            {{ $t('teachers.create_another') }}
          </v-btn>
          <v-btn color="grey" text @click="close">{{ $t('common.close') }}</v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import countryService from '../../../services/country.service';
import schoolService from '../../../services/school.service';
import courseService from '../../../services/course.service';
import teacherService from '../../../services/teacher.service';
import snackbarMixin from '../../../mixins/snackbar';
import { COUNTRIES_CUSTOMER_ID } from '../../../constants/data';

const emptyForm = () => ({
  baseCountryId: null,
  schoolId: null,
  courseIds: [],
  firstname: '',
  lastname: '',
  lastname2nd: '',
  email: '',
  dni: '',
  birthDate: '',
});

export default {
  name: 'CreateTeacherModal',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    schoolId: { type: [String, Number], default: null },
    countryId: { type: [String, Number], default: null },
  },
  data: () => ({
    loading: false,
    valid: false,
    countries: [],
    schools: [],
    courses: [],
    form: emptyForm(),
    dateMenu: false,
    courseSearch: null,
    created: false,
    createdInfo: { username: '', passwordTmp: '', hasEmail: true },
    rules: {
      required: (v) => (v !== null && v !== undefined && v !== '') || 'Requerido.',
      email: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Correo inválido.',
    },
  }),
  computed: {
    lockContext() {
      return !!(this.schoolId && this.countryId);
    },
    createdCredentials() {
      return this.$t('teachers.created_credentials', {
        username: this.createdInfo.username,
        password: this.createdInfo.passwordTmp,
      });
    },
    availableCourses() {
      return this.courses
        .filter((course) => !this.form.courseIds.includes(course.id))
        .map((course) => ({
          id: course.id,
          displayName: this.courseDisplayName(course),
        }));
    },
    selectedCourses() {
      return this.form.courseIds
        .map((id) => this.courses.find((course) => course.id === id))
        .filter(Boolean)
        .map((course) => ({
          id: course.id,
          displayName: this.courseDisplayName(course),
        }));
    },
  },
  async mounted() {
    await this.loadCountries();
    if (this.countryId) {
      this.form.baseCountryId = Number(this.countryId);
      await this.loadSchools(this.form.baseCountryId);
    }
    if (this.schoolId) {
      this.form.schoolId = Number(this.schoolId);
      await this.loadCourses(this.form.schoolId);
    }
  },
  methods: {
    async loadCountries() {
      try {
        const { data } = await countryService.findAll();
        this.countries = (data || []).filter((c) => COUNTRIES_CUSTOMER_ID.includes(c.id));
      } catch (e) {
        this.countries = [];
      }
    },
    async loadSchools(countryId) {
      try {
        const { status, data } = await schoolService.getListByCountry(countryId);
        this.schools = status === 200 ? [...data] : [];
      } catch (e) {
        this.schools = [];
        this.snackbarError(e.message);
      }
    },
    async loadCourses(schoolId) {
      try {
        const { status, data } = await courseService.findAllBySchool(schoolId);
        this.courses = status === 200 ? [...data] : [];
      } catch (e) {
        this.courses = [];
      }
    },
    async onCountryChange() {
      this.form.schoolId = null;
      this.form.courseIds = [];
      this.courseSearch = null;
      this.schools = [];
      this.courses = [];
      if (this.form.baseCountryId) await this.loadSchools(this.form.baseCountryId);
    },
    async onSchoolChange() {
      this.form.courseIds = [];
      this.courseSearch = null;
      this.courses = [];
      if (this.form.schoolId) await this.loadCourses(this.form.schoolId);
    },
    courseDisplayName(course) {
      if (!course || !course.name) return '';
      return course.joinCode ? `${course.name} (${course.joinCode})` : course.name;
    },
    addCourse(courseId) {
      if (courseId == null) return;
      if (!this.form.courseIds.includes(courseId)) {
        this.form.courseIds = [...this.form.courseIds, courseId];
      }
      this.$nextTick(() => {
        this.courseSearch = null;
      });
    },
    removeCourse(courseId) {
      this.form.courseIds = this.form.courseIds.filter((id) => id !== courseId);
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      try {
        const result = await teacherService.create({
          firstname: this.form.firstname,
          lastname: this.form.lastname,
          lastname2nd: this.form.lastname2nd || undefined,
          email: this.form.email || undefined,
          dni: this.form.dni || undefined,
          birthDate: this.form.birthDate || undefined,
          baseCountryId: this.form.baseCountryId,
          schoolId: this.form.schoolId,
          courseIds: this.form.courseIds.length ? this.form.courseIds : undefined,
        });
        this.createdInfo = {
          username: result.content.username,
          passwordTmp: result.content.passwordTmp,
          hasEmail: !!this.form.email,
        };
        this.created = true;
        this.$emit('onSave');
      } catch (error) {
        const status = error.response && error.response.status;
        if (status === 409) {
          this.snackbarError(this.$t('teachers.email_exists'));
        } else {
          const msg = error.response && error.response.data && error.response.data.message;
          this.snackbarError(msg || this.$t('teachers.create_error'));
        }
      } finally {
        this.loading = false;
      }
    },
    resetForAnother() {
      const { baseCountryId, schoolId } = this.form;
      this.form = { ...emptyForm(), baseCountryId, schoolId };
      this.courseSearch = null;
      this.created = false;
      this.createdInfo = { username: '', passwordTmp: '', hasEmail: true };
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    close() {
      this.form = emptyForm();
      this.courseSearch = null;
      this.created = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.$emit('onCancel');
    },
  },
};
</script>

<style scoped>
span.sp {
  font-family: "Space Grotesk" !important;
}
</style>
